import { ArrowDownward, ArrowUpward, Search } from "@mui/icons-material";
import { Box, Input, InputProps, Link, Sheet, Table, Typography } from "@mui/joy";
import { flexRender, Header, Table as TanstackTable } from "@tanstack/react-table";
import { ReactNode } from "react";

// Lowercased and stripped of accents, so "jose" finds "José", as the API's own search does.
export const normalizeForSearch = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

// The search bar's 40px input plus 16px above and below, which keep their background while the
// bar is stuck. The table's header sticks just below it.
const SEARCH_BAR_HEIGHT = 72;

// A search input that sticks to the top of the scrolling panel it's in. Its background should
// match the panel's, so rows scrolling up pass behind it rather than showing through.
//
// The bar brings its own space above and below, so the panel needs no gap around it. Nor does it
// need top padding: a sticky element stops at its scroll container's padding edge, so rows would
// show in the padding above the bar.
//
// `actions`, such as an export button, sit at the bar's right edge.
export const StickySearchBar = ({
  backgroundColor,
  actions,
  ...props
}: InputProps & { backgroundColor: string; actions?: ReactNode }) => (
  <Box
    sx={{
      position: "sticky",
      top: 0,
      zIndex: 2,
      height: SEARCH_BAR_HEIGHT,
      // In a scrolling flex column, the bar would otherwise shrink to its input's height.
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: 1,
      backgroundColor,
    }}
  >
    <Input startDecorator={<Search />} sx={{ flex: 1, maxWidth: 400 }} {...props} />
    {actions ? <Box sx={{ display: "flex", gap: 1, marginLeft: "auto" }}>{actions}</Box> : null}
  </Box>
);

const SortableHeader = <T,>({ header }: { header: Header<T, unknown> }) => {
  const sorted = header.column.getIsSorted();
  return (
    // `overlay` stretches the button over the whole cell, which the table's sticky header has
    // already positioned.
    <th>
      <Link
        component="button"
        overlay
        underline="none"
        textColor={sorted ? "primary.500" : "neutral.800"}
        onClick={header.column.getToggleSortingHandler()}
        endDecorator={sorted ? sorted === "desc" ? <ArrowDownward /> : <ArrowUpward /> : null}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
      </Link>
    </th>
  );
};

// Column widths that don't depend on the rows. Columns that set a `width` in their meta (px) keep
// it; the rest share what's left, down to their `minWidth` (px, default 0). The table is never
// narrower than all of those added up; narrower than that, its panel scrolls. Applies only when
// some column sets a `width`; otherwise the browser sizes the columns to fit their content.
const getColumnSizing = <T,>(table: TanstackTable<T>) => {
  const columns = table.getVisibleLeafColumns().map((column) => column.columnDef.meta);
  if (!columns.some((meta) => meta?.width)) return null;
  return {
    widths: columns.map((meta) => meta?.width),
    minTableWidth: columns.reduce((total, meta) => total + (meta?.width ?? meta?.minWidth ?? 0), 0),
  };
};

// A table whose headers sort it and whose rows each open something. The open row stays
// highlighted. It goes below a `StickySearchBar` in a scrolling panel: its header sticks under
// the search bar while the rows scroll.
//
// The border is drawn on the cells, since the table can't clip its corners with `overflow:
// hidden` without becoming the header's scroll container, and then the header would never stick.
// `backgroundColor` is the panel's, which fills the header's rounded corners.
export default function SortableTable<T>({
  table,
  backgroundColor,
  isRowOpen,
  onRowClick,
}: {
  table: TanstackTable<T>;
  /** A CSS color, not a theme token, since it goes into a `box-shadow`. */
  backgroundColor: string;
  isRowOpen: (row: T) => boolean;
  onRowClick: (row: T) => void;
}) {
  const sizing = getColumnSizing(table);

  return (
    <Sheet>
      <Table
        hoverRow
        stickyHeader
        sx={{
          ...(sizing
            ? { tableLayout: "fixed", width: "100%", minWidth: sizing.minTableWidth }
            : {}),
          "--TableCell-paddingX": "12px",
          "--TableCell-paddingY": "8px",
          "thead th": {
            verticalAlign: "middle",
            backgroundColor: "neutral.100",
            borderTop: "1px solid black",
            borderTopColor: (theme) => theme.vars.palette.neutral["200"],
            top: SEARCH_BAR_HEIGHT,
            zIndex: 100,
          },
          // The stuck header's rounded corners would otherwise let the rows show through. An outer
          // shadow paints only outside the rounded edge, and clipping it to the cell's square box
          // leaves just the corners, filled with the panel's background.
          "thead th:first-child, thead th:last-child": {
            boxShadow: `0 0 0 8px ${backgroundColor}`,
            clipPath: "inset(0)",
          },
          "thead th:first-child": {
            borderTopLeftRadius: "8px",
            borderLeftWidth: "1px",
            borderLeftStyle: "solid",
            borderLeftColor: (theme) => theme.vars.palette.neutral["200"],
          },
          "thead th:last-child": {
            borderTopRightRadius: "8px",
            borderRightWidth: "1px",
            borderRightStyle: "solid",
            borderRightColor: (theme) => theme.vars.palette.neutral["200"],
          },
          "tbody td": { verticalAlign: "middle" },
          "tbody td:first-child": {
            borderLeftWidth: "1px",
            borderLeftStyle: "solid",
            borderLeftColor: (theme) => theme.vars.palette.neutral["200"],
          },
          "tbody td:last-child": {
            borderRightWidth: "1px",
            borderRightStyle: "solid",
            borderRightColor: (theme) => theme.vars.palette.neutral["200"],
          },
          "tbody tr:last-child td": {
            borderBottomWidth: "1px",
            borderBottomStyle: "solid",
            borderBottomColor: (theme) => theme.vars.palette.neutral["200"],
          },
          "tbody tr:last-child td:first-child": { borderBottomLeftRadius: "8px" },
          "tbody tr:last-child td:last-child": { borderBottomRightRadius: "8px" },
          "tbody tr": { cursor: "pointer" },
          "tbody tr.open": { backgroundColor: "primary.50" },
          "--TableRow-hoverBackground": (theme) => theme.vars.palette.neutral["100"],
        }}
      >
        {sizing ? (
          <colgroup>
            {sizing.widths.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
        ) : null}
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <SortableHeader key={header.id} header={header} />
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={isRowOpen(row.original) ? "open" : undefined}
              onClick={() => onRowClick(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
}

// A signed credit amount: green when credits went in, red when they came out.
export const CreditAmount = ({ amount }: { amount: number }) => (
  <Typography
    fontWeight="lg"
    textColor={amount > 0 ? "success.600" : amount < 0 ? "danger.600" : "neutral.500"}
    sx={{ flexShrink: 0 }}
  >
    {amount > 0 ? `+${amount}` : amount < 0 ? `−${-amount}` : amount}
  </Typography>
);
